/**
 * TenantFeatureGate — tenant-side wrapper that blocks a page when
 * the landlord's plan doesn't include the requested feature.
 *
 * Unlike PlanGate (which checks the current user's plan), this
 * checks the landlord's plan via the useLandlordPlan RPC.
 */

import type { ReactNode } from 'react';
import { Lock, Loader2, MessageSquare } from 'lucide-react';
import { hasFeature, type Feature } from '../lib/plans';
import { useLandlordPlan } from '../lib/useLandlordPlan';

interface TenantFeatureGateProps {
    feature: Feature;
    featureName: string;
    children: ReactNode;
}

const FEATURE_LABEL: Partial<Record<Feature, string>> = {
    chat: 'Chat with your landlord',
    maintenance: 'Maintenance requests',
};

export default function TenantFeatureGate({ feature, featureName, children }: TenantFeatureGateProps) {
    const { plan, loading, hasLink } = useLandlordPlan();

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <Loader2 className="animate-spin" size={36} color="#94a3b8" />
            </div>
        );
    }

    // Not linked — let the child page render its own empty state
    // (TenantChat / TenantMaintenance both handle "no landlord link" gracefully).
    if (!hasLink) {
        return <>{children}</>;
    }

    if (hasFeature(plan, feature)) {
        return <>{children}</>;
    }

    const title = FEATURE_LABEL[feature] ?? featureName;

    return (
        <div className="page" style={{ padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
            <div style={{
                maxWidth: 480,
                width: '100%',
                background: '#fff',
                borderRadius: 24,
                overflow: 'hidden',
                boxShadow: '0 20px 40px -12px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.04)',
                border: '1px solid #f1f5f9',
                textAlign: 'center',
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                    padding: '2.5rem 2rem 2rem',
                    color: '#fff',
                }}>
                    <div style={{
                        width: 56,
                        height: 56,
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem',
                    }}>
                        <Lock size={26} color="#fff" />
                    </div>
                    <h2 style={{ margin: '0 0 8px', fontSize: '1.5rem', fontWeight: 900, letterSpacing: '-0.02em' }}>
                        {title} is not available
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.9375rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>
                        Your landlord's current plan doesn't include this feature.
                    </p>
                </div>
                <div style={{ padding: '1.75rem 2rem', fontSize: '0.875rem', color: '#64748b', lineHeight: 1.6 }}>
                    {feature === 'chat' ? (
                        <>
                            <MessageSquare size={18} style={{ marginBottom: 8, color: '#94a3b8' }} /><br />
                            If you need to reach your landlord, please use email or phone.
                            Chat becomes available when your landlord upgrades their plan.
                        </>
                    ) : (
                        <>
                            Please contact your landlord directly. This feature will be
                            available in the portal when your landlord upgrades their plan.
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
